import type { HotManifest } from "./types";

/** Polls a manifest on a fixed interval; single-flight (aborts the previous request
 * on each tick). Generic over the manifest shape — the mosaic manifest
 * (mosaicTypes.ts) is wire-compatible with HotManifest (workers/CONTRACTS.md: "LIVE
 * mosaic/conus.json (HotManifest shape...)") but not identical, so `T` defaults to
 * HotManifest for the existing per-station callers and mosaicController.ts
 * instantiates `ManifestPoller<MosaicManifest>` instead of casting. */
export class ManifestPoller<T = HotManifest> {
  private timer: ReturnType<typeof setInterval> | undefined;
  private controller: AbortController | undefined;

  constructor(
    private getUrl: () => string,
    private onManifest: (manifest: T) => void,
    private onError: (err: unknown) => void,
    private intervalMs = 10_000,
  ) {}

  start(): void {
    this.stop();
    void this.poll();
    this.timer = setInterval(() => void this.poll(), this.intervalMs);
  }

  stop(): void {
    if (this.timer !== undefined) clearInterval(this.timer);
    this.timer = undefined;
    this.controller?.abort();
    this.controller = undefined;
  }

  private async poll(): Promise<void> {
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    try {
      const res = await fetch(this.getUrl(), { signal: controller.signal, cache: "no-store" });
      if (res.status === 404) {
        // Staged rollout: a station without a manifest simply isn't baking yet.
        throw new Error("station not yet active — rollout is staged; try KAMX");
      }
      if (!res.ok) throw new Error(`manifest fetch failed: ${res.status}`);
      const manifest = (await res.json()) as T;
      this.onManifest(manifest);
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      this.onError(err);
    }
  }
}
